import { ApolloError, ApolloLink, Observable, Operation } from '@apollo/client'
import { RetryLink } from '@apollo/client/link/retry'
import { getMainDefinition } from '@apollo/client/utilities'

// ENGG-10325 — a request that got no HTTP response at all (the browser's
// "Failed to fetch") is a dropped connection, not a server error. On some
// networks (an admin on a corporate VPN) the first request after a minute idle
// fails this way and requests keep failing for several seconds. Instead of
// showing an error and losing the save, those requests are retried with
// backoff (1s, 2s, 4s, 8s, 8s by default) before the error link sees them.

// True for a request that never got a response. Responses with a status (401,
// 403, 422, 500…) carry statusCode and stay with the error link; an abort is
// the app cancelling the request.
export const isConnectionError = (error: any): boolean =>
  !!error && error.statusCode === undefined && error.name !== 'AbortError'

const isMutation = (operation: Pick<Operation, 'query'>): boolean => {
  const definition = getMainDefinition(operation.query)
  return (
    definition.kind === 'OperationDefinition' &&
    definition.operation === 'mutation'
  )
}

// The fields a mutation writes, to tell whether a newer mutation overwrites a
// waiting retry. A mutation with an `attributes` object writes each of those
// attributes on its target (its other variables: id, locale…); the task
// drawer's autosave sends only the attributes that changed. Any other mutation
// counts as writing everything its operation writes.
const writtenFields = (operation: Operation): string[] => {
  const { attributes, ...target } = operation.variables
  if (
    !attributes ||
    typeof attributes !== 'object' ||
    Array.isArray(attributes)
  ) {
    return [operation.operationName]
  }
  const prefix = `${operation.operationName} ${JSON.stringify(target)}`
  return Object.keys(attributes).map((key) => `${prefix} ${key}`)
}

export const SERVER_ERROR_MESSAGE =
  'An error occurred while accessing the server'
const CHANGE_NOT_SAVED_MESSAGE =
  "Lost connection to the server, so your last change wasn't saved. Please check your connection and try again."

// What the user sees once the retries run out.
export const connectionLostMessage = (
  operation: Pick<Operation, 'query'>,
): string =>
  isMutation(operation)
    ? CHANGE_NOT_SAVED_MESSAGE
    : 'Lost connection to the server. Please check your connection and try again.'

// For a save that handles its own error after the error link: the server's
// validation message if it sent one, otherwise which way the request failed.
export const saveErrorMessage = (error: ApolloError): string =>
  error.graphQLErrors?.[0]?.message ||
  (isConnectionError(error.networkError)
    ? CHANGE_NOT_SAVED_MESSAGE
    : SERVER_ERROR_MESSAGE)

type Options = {
  initialDelayMs?: number
  maxDelayMs?: number
  maxAttempts?: number
  fastFailureMs?: number
}

export function createConnectionRetryLink({
  initialDelayMs = 1000,
  maxDelayMs = 8000,
  maxAttempts = 6,
  fastFailureMs = 2000,
}: Options = {}): ApolloLink {
  let mutationCount = 0
  // The number of the latest mutation sent for each written field.
  const latestWriter = new Map<string, number>()

  const isSuperseded = (operation: Operation) => {
    const { mutationNumber } = operation.getContext()
    return writtenFields(operation).some(
      (field) => latestWriter.get(field) !== mutationNumber,
    )
  }

  // Numbers each mutation once, before any retry, so a retry can tell whether
  // a newer mutation has written one of its fields since.
  const numberMutations = new ApolloLink((operation, forward) => {
    if (isMutation(operation)) {
      mutationCount += 1
      operation.setContext({ mutationNumber: mutationCount })
      writtenFields(operation).forEach((field) =>
        latestWriter.set(field, mutationCount),
      )
    }
    return forward(operation)
  })

  const retry = new RetryLink({
    // No jitter: concurrent requests keep the order they were sent in.
    delay: { initial: initialDelayMs, max: maxDelayMs, jitter: false },
    attempts: {
      max: maxAttempts,
      retryIf: (error, operation) => {
        if (!isConnectionError(error)) return false
        if (!isMutation(operation)) return true
        // A mutation that failed slowly may have reached the server: a request
        // cut off by the load balancer's timeout is also "Failed to fetch".
        const failedFast =
          Date.now() - operation.getContext().attemptStartedAt < fastFailureMs
        return failedFast && !isSuperseded(operation)
      },
    },
  })

  // Runs on every attempt. Stamps its start for the fast-failure check, and
  // drops a mutation retry if a newer mutation wrote one of its fields while it
  // waited: resending the older value would overwrite the newer one.
  const startAttempt = new ApolloLink((operation, forward) => {
    const { attemptStartedAt, mutationNumber } = operation.getContext()
    const isRetry = attemptStartedAt !== undefined
    if (isRetry && mutationNumber !== undefined && isSuperseded(operation)) {
      return new Observable((observer) => {
        observer.error(new TypeError('Failed to fetch'))
      })
    }
    operation.setContext({ attemptStartedAt: Date.now() })
    return forward(operation)
  })

  return ApolloLink.from([numberMutations, retry, startAttempt])
}
